import React, { useEffect, useRef, useState } from "react";

const ColouredArc = ({
  currentTemp = 30,
  setTemp = 35,
  minTemp = 5,
  maxTemp = 41,
  state = 1, // 1 = heating, 2 = cooling, else idle
}) => {
  const startAngle = 165;
  const sweepAngle = 210;

  const [animatedCurrentAngle, setAnimatedCurrentAngle] = useState(0);
  const [animatedSetAngle, setAnimatedSetAngle] = useState(0);

  const reqRef = useRef();

  const arcColour =
    state === 1 ? "#FF6E00" : state === 2 ? "#07A7FF" : "#CCE2F2";

  const polarToCartesian = (cx, cy, radius, angle) => {
    const rad = (angle * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
    };
  };

  const describeArc = (cx, cy, radius, startAngle, sweepAngle) => {
    const start = polarToCartesian(cx, cy, radius, startAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle + sweepAngle);
    const largeArcFlag = sweepAngle > 180 ? 1 : 0;

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
  };

  const tempToAngle = (temp) =>
    ((temp - minTemp) / (maxTemp - minTemp)) * sweepAngle;

  // Animate between angles with requestAnimationFrame
  const animateValue = (from, to, duration, setter) => {
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const value = from + (to - from) * progress;
      setter(value);

      if (progress < 1) {
        reqRef.current = requestAnimationFrame(tick);
      }
    };

    reqRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const currentTarget = tempToAngle(currentTemp);
    const setTarget = tempToAngle(setTemp);

    animateValue(animatedCurrentAngle, currentTarget, 3000, setAnimatedCurrentAngle);
    animateValue(animatedSetAngle, setTarget, 1500, setAnimatedSetAngle);

    return () => cancelAnimationFrame(reqRef.current);
  }, [currentTemp, setTemp]);

  const size = 280;
  const strokeWidth = 10;
  const radius = size / 2 - strokeWidth;
  const center = size / 2;

  const handle = polarToCartesian(
    center,
    center,
    radius,
    startAngle + animatedCurrentAngle
  );

  const dot = polarToCartesian(
    center,
    center,
    radius,
    startAngle + animatedSetAngle + 3
  );

  const handleAngleRad = ((startAngle + animatedCurrentAngle) * Math.PI) / 180;
  const tx = Math.cos(handleAngleRad);
  const ty = Math.sin(handleAngleRad);

  const handleLength = 10; // half length of bar
  const x1 = handle.x - tx * handleLength;
  const y1 = handle.y - ty * handleLength;
  const x2 = handle.x + tx * handleLength;
  const y2 = handle.y + ty * handleLength;


  return (
    <svg width={size} height={size} className="overflow-visible">
      {/* Grey background arc */}
      <path
        d={describeArc(center, center, radius, startAngle, sweepAngle)}
        stroke="darkgray"
        strokeWidth={20}
        fill="none"
        strokeLinecap="round"
      />

      {/* Current temp arc */}
      <path
        d={describeArc(center, center, radius, startAngle, animatedCurrentAngle)}
        stroke={arcColour}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
      />

      {/* White arc to set temp */}
      {animatedSetAngle > animatedCurrentAngle && (
        <path
          d={describeArc(
            center,
            center,
            radius,
            startAngle + animatedCurrentAngle + 3,
            animatedSetAngle - animatedCurrentAngle
          )}
          stroke="white"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
        />
      )}

      {/* Handle bar */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="white"
        strokeWidth={10}
        strokeLinecap="round"
      />

      {/* Dot for set temp */}
      <circle cx={dot.x} cy={dot.y} r={4} fill={arcColour} />
    </svg>
  );
};

export default ColouredArc;
